import { bind, Component, h, signal, tag } from "omi";
import classNames from "classnames";
import isEmpty from "lodash-es/isEmpty";

import { getElementsFromSlot } from "@/utils/elements.ts";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

export type TabsProps = {
  active: string;
  largetabs?: boolean;
  headingsize?: "sm" | "md";
  embedded?: boolean;
};

@tag("nw-tabs")
export class NWTabs extends Component<TabsProps> {
  static css = [
    tailwind,
    `
    .tabs-content-container {
      min-height: 50px;
      position: relative;
    }
    
    /* Prevent Firefox layout shift during animations */
    .tabs-content-container::after {
      content: "";
      display: block;
      clear: both;
    }
    `,
  ];

  static propTypes = {
    active: String,
    largetabs: Boolean,
    headingsize: String,
    embedded: Boolean,
  };

  static defaultProps = {
    headingsize: "md",
    largetabs: false,
    embedded: false,
  };

  private tabs = signal<NWTab[]>([]);

  ready(): void {
    this.tabs.value = getElementsFromSlot<NWTab>(this.shadowRoot, "tab-slot", ["nw-tab*"]);
  }

  uninstall(): void {
    this.tabs.value = [];
  }

  /**
   * Handles tab selection and triggers appropriate events
   * @param index - The index of the tab being selected
   */
  private handleTabClick = (index: number): void => {
    const tabs = this.tabs.value;
    if (index < 0 || index >= tabs.length) {
      return;
    }

    const newTab = tabs[index];
    const newTabId = newTab?.id;

    if (!newTabId) {
      return;
    }

    // Prevent firing event if already active (check against current prop)
    if (newTabId === this.props.active) {
      return;
    }

    this.fire("tab-change", { tabId: newTabId });

    const currentIndex = tabs.findIndex(tab => tab.id === this.props.active);
    if (currentIndex !== -1) {
      this.trackTabChange(currentIndex, index);
    }
  };

  /**
   * Track analytics for tab changes
   * @param fromIndex - Index of the previously active tab
   * @param toIndex - Index of the newly selected tab
   */
  @bind
  private trackTabChange(fromIndex: number, toIndex: number): void {
    const projectId = Store.project.id.value;
    if (!projectId) {
      return;
    }

    const currentTabs = this.tabs.value;
    if (fromIndex < 0 || fromIndex >= currentTabs.length || toIndex < 0 || toIndex >= currentTabs.length) {
      return;
    }

    const fromTab = currentTabs[fromIndex];
    const toTab = currentTabs[toIndex];

    const fromTabId = fromTab.id;
    const toTabId = toTab.id;

    const fromHeading = fromTab.getAttribute("heading") || "";
    const toHeading = toTab.getAttribute("heading") || "";

    if (isEmpty(fromHeading) || isEmpty(toHeading)) {
      return;
    }

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackContentTabsChanged(projectId, fromTabId, fromHeading, toTabId, toHeading);
    });
  }

  @bind
  private renderTabButtons() {
    const tabs = this.tabs.value;
    if (tabs.length === 0) {
      return null;
    }

    const activeTabId = this.props.active;
    const headingSize = this.props.headingsize as TabsProps["headingsize"];
    const useLargeTabs = this.props.largetabs === true;

    return (
      <div role="tablist" class="flex flex-1 border-b border-gray-200 h-auto items-center relative flex-row">
        {tabs.map((tab, index) => this.renderTabButton(tab, index, activeTabId, headingSize, useLargeTabs))}
      </div>
    );
  }

  private renderTabButton(
    tab: NWTab,
    index: number,
    activeTabId: string,
    headingSize: TabsProps["headingsize"],
    useLargeTabs: boolean,
  ) {
    const isActive = tab.id === activeTabId;
    const tabCount = this.tabs.value.length;

    return (
      <button
        key={tab.id}
        role="tab"
        aria-selected={isActive}
        class={classNames(
          "px-4 py-2 font-semibold",
          "border-b-2 -mb-[2px] transition-all",
          isActive
            ? "border-brand-500 text-leather"
            : "border-transparent text-quaternary hover:text-secondary hover:border-gray-300",
          { [`w-1/${tabCount}`]: useLargeTabs },
          { "text-sm": headingSize === "sm" },
          { "text-md": headingSize === "md" },
        )}
        onClick={() => this.handleTabClick(index)}
      >
        {tab.getAttribute("heading")}
      </button>
    );
  }

  render() {
    return (
      <div class="w-full overflow-x-hidden animate-fadeInFast">
        {this.renderTabButtons()}
        <div class="tabs-content-container mt-5 pb-10 px-10">
          <slot id="tab-slot"></slot>
        </div>
      </div>
    );
  }
}

type TabProps = {
  id: string;
  heading: string;
};

@tag("nw-tab")
export class NWTab extends Component<TabProps> {
  static css = [
    tailwind,
    `
    :host {
      display: block;
      width: 100%;
    }
    
    .tab-content {
      width: 100%;
      /* Fix Firefox positioning */
      transform: translateZ(0);
    }
    `,
  ];

  static propTypes = {
    id: String,
    heading: String,
  };

  private animationDirection = signal<string | undefined>(undefined);
  private isVisible = signal<boolean>(false);

  installed(): void {
    const parentTabs = this.parentElement?.closest("nw-tabs");
    if (parentTabs) {
      const activeTabId = parentTabs.getAttribute("active") || "";
      this.isVisible.value = this.id === activeTabId;
    }
  }

  animateInRight(): void {
    this.animationDirection.value = "animate-slideInRightFadeIn";
  }

  animateInLeft(): void {
    this.animationDirection.value = "animate-slideInLeftFadeIn";
  }

  animateOutRight(): void {
    this.animationDirection.value = "animate-slideOutRightFadeOut";
  }

  animateOutLeft(): void {
    this.animationDirection.value = "animate-slideOutLeftFadeOut";
  }

  hide(): void {
    this.isVisible.value = false;
  }

  show(): void {
    this.isVisible.value = true;
  }

  render() {
    return (
      <div className={classNames("tab-content", this.animationDirection.value, { hidden: !this.isVisible.value })}>
        <slot></slot>
      </div>
    );
  }
}
